import { useState, useRef, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { api, ApiUnreachableError } from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { ToastAction } from '@/components/ui/toast'
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form'

interface AttachedImage {
  file: File
  preview: string
  url?: string
}

// The only declarative form field here is the instructions textarea - attached
// images are populated by paste/file-select side effects and uploaded
// asynchronously on submit, so that part keeps its own useState (see
// attachedImages below) rather than being modeled as a form field.
const instructionsFormSchema = z.object({
  instructions: z.string(),
})

type InstructionsFormValues = z.infer<typeof instructionsFormSchema>

interface InstructionsModalProps {
  open: boolean
  messageId?: string | null
  initialInstructions?: string
  onClose: () => void
  onRewrite: (messageId: string, instructions: string, imageUrls: string[]) => Promise<void> | void
}

export function InstructionsModal({
  open,
  messageId,
  initialInstructions = '',
  onClose,
  onRewrite,
}: InstructionsModalProps) {
  const { toast } = useToast()
  const form = useForm<InstructionsFormValues>({
    resolver: zodResolver(instructionsFormSchema),
    defaultValues: { instructions: initialInstructions },
  })
  const [attachedImages, setAttachedImages] = useState<AttachedImage[]>([]) // Array of { file: File, preview: string, url?: string }
  const attachedImagesRef = useRef(attachedImages)

  // Reset input/images when the modal transitions to open. Adjusted during
  // render (rather than in an effect) per React's guidance for resetting
  // state in response to a prop change: https://react.dev/learn/you-might-not-need-an-effect
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      form.reset({ instructions: initialInstructions })
      setAttachedImages([])
    }
  }

  useEffect(() => {
    attachedImagesRef.current = attachedImages
  }, [attachedImages])

  // Cleanup object URLs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      // Revoke all object URLs when component unmounts
      attachedImagesRef.current.forEach(img => {
        if (img.preview) {
          URL.revokeObjectURL(img.preview)
        }
      })
    }
  }, [])

  // Handle image paste
  const handleImagePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items
    if (!items) return

    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault()
        const file = item.getAsFile()
        if (file) {
          const preview = URL.createObjectURL(file)
          setAttachedImages(prev => [...prev, { file, preview }])
        }
      }
    }
  }

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const preview = URL.createObjectURL(file)
        setAttachedImages(prev => [...prev, { file, preview }])
      }
    })
    // Reset input so same file can be selected again
    e.target.value = ''
  }

  // Remove image
  const handleRemoveImage = (index: number) => {
    setAttachedImages(prev => {
      const newImages = [...prev]
      URL.revokeObjectURL(newImages[index].preview)
      newImages.splice(index, 1)
      return newImages
    })
  }

  // Handle rewrite (save instructions and regenerate)
  const handleRewrite = async (values: InstructionsFormValues) => {
    if (!messageId) return

    try {
      // Upload images first, persisting each URL to state as soon as its
      // upload succeeds (rather than only once the whole loop finishes) so
      // that if a later image's upload fails, the earlier ones aren't lost
      // and a retry doesn't re-upload (and orphan) them.
      const imageUrls: string[] = []
      const finalImages: AttachedImage[] = [...attachedImages]
      for (let i = 0; i < finalImages.length; i++) {
        const image = finalImages[i]
        if (!image.url) {
          // Upload to backend using API client
          const data = await api.uploadImage(image.file)
          finalImages[i] = { ...image, url: data.url }
          imageUrls.push(data.url)
          setAttachedImages([...finalImages])
        } else {
          imageUrls.push(image.url)
        }
      }

      // Clean up image previews before clearing
      finalImages.forEach(img => URL.revokeObjectURL(img.preview))
      const finalInstructions = values.instructions
      const finalImageUrls = imageUrls

      // Close modal and clear state
      form.reset({ instructions: '' })
      setAttachedImages([])
      onClose()

      // Call onRewrite with instructions and images
      await onRewrite(messageId, finalInstructions, finalImageUrls)
    } catch (error: unknown) {
      console.error('Failed to retry with instructions:', error)
      const isUnreachable = error instanceof ApiUnreachableError
      const err = error as { message?: string; baseUrl?: string }
      toast({
        title: 'Error',
        description: err?.message || 'Failed to upload images or regenerate suggestion',
        variant: 'destructive',
        action: isUnreachable ? (
          <ToastAction
            altText="Re-authenticate"
            onClick={() => window.open(err.baseUrl, '_blank', 'noopener,noreferrer')}
          >
            Re-authenticate
          </ToastAction>
        ) : undefined,
      })
    }
  }

  // Handle cancel instructions
  const handleCancel = () => {
    // Clean up image previews
    attachedImages.forEach(img => URL.revokeObjectURL(img.preview))
    form.reset({ instructions: '' })
    setAttachedImages([])
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add instructions</DialogTitle>
          <DialogDescription>
            Provide additional context or instructions for reply generation for this message.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleRewrite)} noValidate>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <FormField
                  control={form.control}
                  name="instructions"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <textarea
                          {...field}
                          onPaste={handleImagePaste}
                          placeholder="Enter instructions... (You can paste images here)"
                          className="w-full min-h-[200px] px-3 py-2 border border-border rounded-md bg-surface text-text placeholder:text-textMuted focus:outline-hidden focus:ring-2 focus:ring-mint"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="image-upload-input"
                    multiple
                  />
                  <label
                    htmlFor="image-upload-input"
                    className="text-sm text-textMuted hover:text-text cursor-pointer underline"
                  >
                    Attach images
                  </label>
                  <span className="text-sm text-textMuted">or paste images directly</span>
                </div>
              </div>

              {/* Image previews */}
              {attachedImages.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-text">Attached images:</div>
                  <div className="grid grid-cols-3 gap-2">
                    {attachedImages.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image.preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-24 object-cover rounded-md border border-border"
                        />
                        <button
                          onClick={() => handleRemoveImage(index)}
                          className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          type="button"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="flex-row gap-2 sm:flex-row">
              <Button type="submit" className="flex-1">
                Rewrite
              </Button>
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
